
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Download, FileText } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';

// Define our export record type
interface ExportRecord {
  id: string;
  file_name: string;
  file_path: string | null;
  document_type: string;
  created_at: string;
  created_by: string | null;
  metadata: {
    state?: string;
    format?: string;
    count?: number;
    exported_at?: string;
    filters?: {
      state?: string;
      courseType?: string;
      startDate?: string;
      endDate?: string;
      includeInactive?: boolean;
    };
  } | null;
}

export function ExportHistory() {
  const [stateFilter, setStateFilter] = useState<string>("all");
  
  const { data: exportHistory, isLoading } = useQuery({
    queryKey: ['exportHistory', stateFilter],
    queryFn: async () => {
      let query = supabase
        .from('document_records')
        .select('*')
        .eq('document_type', 'certificate_export')
        .order('created_at', { ascending: false });
        
      if (stateFilter && stateFilter !== "all") {
        // Use type-safe way to filter by state in metadata
        query = query.filter('metadata->state', 'eq', stateFilter);
      }
        
      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as ExportRecord[];
    },
  });

  const { data: states } = useQuery({
    queryKey: ['exportStates'],
    queryFn: async () => {
      try {
        // Fetch document records and extract states from metadata
        const { data, error } = await supabase
          .from('document_records')
          .select('metadata')
          .eq('document_type', 'certificate_export');
          
        if (error) throw error;
        
        // Extract unique states with proper type safety
        const stateValues: string[] = [];
        
        if (data) {
          data.forEach(item => {
            // Properly type and access the metadata, which could be a JSON object with a state property
            const metadata = item.metadata as Record<string, any> | null;
            const state = metadata?.state;
            
            if (state && typeof state === 'string' && state.trim() !== '' && state !== 'null') {
              if (!stateValues.includes(state)) {
                stateValues.push(state);
              }
            }
          });
        }
        
        return stateValues.sort();
      } catch (error) {
        console.error('Error fetching states:', error);
        return [];
      }
    },
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Export History</CardTitle>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Filter by state:</span>
          <Select
            value={stateFilter}
            onValueChange={setStateFilter}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="All States" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All States</SelectItem>
              {states?.map((state) => (
                <SelectItem key={state} value={state}>{state}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : exportHistory && exportHistory.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Files</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Format</TableHead>
                <TableHead>Created By</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {exportHistory.map((export_record) => (
                <TableRow key={export_record.id}>
                  <TableCell>
                    {format(new Date(export_record.created_at), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell>{export_record.file_name}</TableCell>
                  <TableCell>
                    {export_record.metadata?.state || 'All'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {export_record.metadata?.format || 'CSV'}
                    </Badge>
                  </TableCell>
                  <TableCell>Admin</TableCell>
                  <TableCell className="text-right">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="gap-1"
                      disabled={!export_record.file_path}
                    >
                      <Download className="h-4 w-4" />
                      Download
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-8 flex flex-col items-center text-muted-foreground">
            <FileText className="h-12 w-12 mb-3 opacity-40" />
            <p>No export history found</p>
            <p className="text-sm max-w-md mt-1">
              When you export certificate records, they will appear here for easy access later.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
